/**
 * VoiceRecorder Component
 * Audio recording interface for testing speech-to-text transcription
 */

import React from 'react';
import { FiMic, FiSquare, FiLoader } from 'react-icons/fi';

interface VoiceRecorderProps {
  isRecording: boolean;
  duration: number;
  transcription: string | null;
  onToggleRecording: () => void;
  disabled?: boolean;
  className?: string;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  isRecording,
  duration,
  transcription,
  onToggleRecording,
  disabled = false,
  className = ''
}) => {
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white">Voice Recording</h3>
        <p className="text-sm text-gray-400 mt-1">Test speech-to-text transcription</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Recording Status */}
        {isRecording && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                  <div className="absolute inset-0 w-4 h-4 bg-red-500 rounded-full animate-ping" />
                </div>
                <span className="text-red-400 font-medium">Recording</span>
              </div>
              <span className="text-red-400 font-mono text-lg">{formatDuration(duration)}</span>
            </div>
          </div>
        )}

        {/* Recording Control Button */}
        <button
          onClick={onToggleRecording}
          disabled={disabled}
          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-medium transition-all ${
            disabled
              ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
              : isRecording
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {isRecording ? (
            <>
              <FiSquare size={24} />
              <span>Stop Recording</span>
            </>
          ) : (
            <>
              <FiMic size={24} />
              <span>Start Recording</span>
            </>
          )}
        </button>

        {/* Transcription Result */}
        {transcription && (
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-300">Transcription:</span>
            </div>
            <p className="text-gray-300 leading-relaxed">{transcription}</p>
          </div>
        )}

        {/* No transcription placeholder */}
        {!transcription && !isRecording && (
          <div className="bg-slate-900/50 rounded-lg p-8 border border-dashed border-slate-700">
            <div className="text-center">
              <FiMic className="mx-auto text-gray-500 mb-3" size={48} />
              <p className="text-gray-400 text-sm">No recording yet</p>
              <p className="text-gray-500 text-xs mt-1">
                Click "Start Recording" to begin
              </p>
            </div>
          </div>
        )}

        {/* Processing indicator */}
        {!isRecording && duration > 0 && !transcription && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FiLoader className="animate-spin text-blue-400" size={20} />
              <span className="text-blue-400 text-sm">Processing transcription...</span>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-gray-400">
            <strong>Tip:</strong> Speak clearly and at a normal pace. The transcription will appear
            automatically after you stop recording.
          </p>
        </div>

        {/* Browser compatibility warning */}
        {disabled && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-xs text-yellow-400">
              Please select a voice first or check if your browser supports audio recording
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceRecorder;
